<template>
  <Disclosure
    v-if="userStoreWithRefs.currentUser"
    v-slot="{ open }"
    as="nav"
    class="border border-gray-200"
    aria-label="Global"
  >
    <div class="mx-auto px-2 sm:px-4 lg:px-8">
      <div class="flex h-16 justify-between">
        <div class="flex items-center px-2 lg:px-0">
          <img class="w-[140px]" src="~/assets/images/sa-logo.png" alt="SAHelp logo" />
        </div>
        <div class="hidden lg:ml-8 lg:flex lg:space-x-4">
          <template v-for="item in navigation" :key="item.name">
            <NuxtLink
              v-if="item.href === '/reports/fund-transactions'"
              :to="item.href"
              @click="fundTransactionStore.setShownSelectFiltersModal(false)"
              class="py-5 px-3 font-proxima text-black text-sm font-semibold hover:text-red-500 hover:border-b-4 hover:border-red-500"
              :class="[
                router.currentRoute.value.path == item.href ||
                (router.currentRoute.value.path.includes(item.href) && item.href !== '/' && item.href !== '')
                  ? 'text-red-500 border-b-4 border-red-500'
                  : '',
              ]"
              >{{ item.name }}
            </NuxtLink>
            <NuxtLink
              v-else-if="!item.child"
              :to="item.href"
              class="py-5 px-3 font-proxima text-black text-sm font-semibold hover:text-red-500 hover:border-b-4 hover:border-red-500"
              :class="[
                router.currentRoute.value.path == item.href ||
                (router.currentRoute.value.path.includes(item.href) && item.href !== '/' && item.href !== '')
                  ? 'text-red-500 border-b-4 border-red-500'
                  : '',
              ]"
              >{{ item.name }}
            </NuxtLink>
            <Menu v-else as="div" class="relative inline-block text-left">
              <div>
                <MenuButton as="template" v-slot="{ open }">
                  <button
                    class="px-3 font-proxima text-black text-sm font-semibold hover:text-red-500 hover:border-b-4 hover:border-red-500"
                    :class="{
                      'text-red-500 border-b-4 border-red-500': open,
                      'pt-5': item.name === 'Assistance Requests',
                      'py-5': item.name !== 'Assistance Requests',
                    }"
                  >
                    {{ item.name }}
                  </button>
                </MenuButton>
              </div>

              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0"
              >
                <MenuItems
                  class="z-10 absolute right-0 mt-1 w-64 origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none"
                >
                  <div class="px-1 py-1">
                    <MenuItem v-for="child in item.child" :key="child.name" v-slot="{ active }" as="div">
                      <div class="py-2">
                        <AdminReportsBtn v-if="child.name === 'Custom Reports'" />
                        <!-- Third level: hover-to-open side flyout (e.g. Reports > Distributions > {assistance type}) -->
                        <div v-else-if="child.child" class="group relative">
                          <button
                            type="button"
                            class="flex w-full items-center justify-between py-0 px-3 font-proxima text-black text-sm font-semibold hover:text-red-500"
                          >
                            <span>{{ child.name }}</span>
                            <Icon name="heroicons:chevron-left-20-solid" class="h-4 w-4" />
                          </button>
                          <div
                            class="invisible absolute right-full top-0 w-64 origin-top-right rounded-md bg-white opacity-0 shadow-lg ring-1 ring-black/5 transition duration-100 group-hover:visible group-hover:opacity-100"
                          >
                            <div class="px-1 py-1">
                              <NuxtLink
                                v-for="leaf in child.child"
                                :key="leaf.name"
                                :href="leaf.href"
                                class="block py-2 px-3 font-proxima text-black text-sm font-semibold hover:text-red-500"
                                >{{ leaf.name }}
                              </NuxtLink>
                            </div>
                          </div>
                        </div>
                        <NuxtLink
                          v-else
                          :href="child.href"
                          class="py-5 px-3 font-proxima text-black text-sm font-semibold hover:text-red-500"
                          >{{ child.name }}
                        </NuxtLink>
                      </div>
                    </MenuItem>
                  </div>
                </MenuItems>
              </transition>
            </Menu>
          </template>
          <button
            v-if="userOnlyHasPmtProcPerms"
            class="py-5 px-3 font-proxima text-black text-sm font-semibold hover:text-red-500 hover:border-b-4 hover:border-red-500"
            @click="showWarning"
          >
            Appointments
          </button>
        </div>
        <div class="flex items-center lg:hidden">
          <DisclosureButton
            class="inline-flex items-center justify-center rounded-md p-2 text-sky-200 hover:bg-sky-400 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
          >
            <Icon v-if="!open" name="heroicons:bars-3-solid" class="block h-6 w-6 text-black font-bold" />
            <Icon v-else name="heroicons:x-mark-solid" class="block h-6 w-6 text-black font-bold" />
          </DisclosureButton>
        </div>
      </div>
    </div>
    <DisclosurePanel v-slot="{ close }" class="lg:hidden">
      <div class="space-y-1 px-2 pt-2 pb-3">
        <template v-for="item in navigation" :key="item.name">
          <!-- Leaf link -->
          <NuxtLink
            v-if="!item.child"
            :to="item.href"
            class="block rounded-md py-2 px-3 text-base font-medium text-black hover:text-red-500"
            :class="[isActive(item.href) ? 'text-red-500 border-b-4 border-red-500' : '']"
            @click="onMobileLinkClick(item.href, close)"
            >{{ item.name }}
          </NuxtLink>
          <!-- Parent with submenu: nested disclosure -->
          <Disclosure v-else v-slot="{ open: subOpen }" as="div">
            <DisclosureButton
              class="flex w-full items-center justify-between rounded-md py-2 px-3 text-base font-medium text-black hover:text-red-500"
            >
              <span>{{ item.name }}</span>
              <Icon
                name="heroicons:chevron-down-20-solid"
                class="h-5 w-5 transition-transform"
                :class="{ 'rotate-180': subOpen }"
              />
            </DisclosureButton>
            <DisclosurePanel class="space-y-1 pl-4">
              <template v-for="child in item.child" :key="child.name">
                <AdminReportsBtn v-if="child.name === 'Custom Reports'" mobile @click="close()" />
                <!-- Third level -->
                <Disclosure v-else-if="child.child" v-slot="{ open: leafOpen }" as="div">
                  <DisclosureButton
                    class="flex w-full items-center justify-between rounded-md py-2 px-3 text-base font-medium text-black hover:text-red-500"
                  >
                    <span>{{ child.name }}</span>
                    <Icon
                      name="heroicons:chevron-down-20-solid"
                      class="h-5 w-5 transition-transform"
                      :class="{ 'rotate-180': leafOpen }"
                    />
                  </DisclosureButton>
                  <DisclosurePanel class="space-y-1 pl-4">
                    <NuxtLink
                      v-for="leaf in child.child"
                      :key="leaf.name"
                      :to="leaf.href"
                      class="block rounded-md py-2 px-3 text-base font-medium text-black hover:text-red-500"
                      :class="[isActive(leaf.href) ? 'text-red-500 border-b-4 border-red-500' : '']"
                      @click="onMobileLinkClick(leaf.href, close)"
                      >{{ leaf.name }}
                    </NuxtLink>
                  </DisclosurePanel>
                </Disclosure>
                <NuxtLink
                  v-else
                  :to="child.href"
                  class="block rounded-md py-2 px-3 text-base font-medium text-black hover:text-red-500"
                  :class="[isActive(child.href) ? 'text-red-500 border-b-4 border-red-500' : '']"
                  @click="onMobileLinkClick(child.href, close)"
                  >{{ child.name }}
                </NuxtLink>
              </template>
            </DisclosurePanel>
          </Disclosure>
        </template>
        <button
          v-if="userOnlyHasPmtProcPerms"
          class="block w-full text-left rounded-md py-2 px-3 text-base font-medium text-black hover:text-red-500"
          @click="showWarning"
        >
          Appointments
        </button>
      </div>
    </DisclosurePanel>
  </Disclosure>
</template>
<script setup lang="ts">
import { Disclosure, DisclosureButton, DisclosurePanel, Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import { useIntegrationStore } from '~/composables/stores/IntegrationStore';
import { useQuery } from '@tanstack/vue-query';
import { integration_faqs } from '~/composables/consts';
import { useApplicationStore } from '~/composables/stores/ApplicationStore';
import { useAssistanceTypeStore } from '~/composables/stores/AssistanceTypeStore';

const userStore = useUserStore();
const userStoreWithRefs = storeToRefs(userStore);

const router = useRouter();
const p = ref(userStore.getUserPermissionTypes() ?? []);
const userOnlyHasPmtProcPerms = computed(() => {
  return (
    p.value.filter((value) => value !== 3).length === 0 &&
    !userStoreWithRefs.isAdminForLocation.value &&
    !userStoreWithRefs.hasOversight.value
  );
});

const integrationStore = useIntegrationStore();
const { data } = useQuery(['integrations', 'get'], () => IntegrationModel.checkIntegrationEnabled());

// Every assistance type the user is allowed to access, used to build the Appointments
// distribution submenu generically (instead of probing the food/disaster ids one by one).
const { data: allowedData } = useQuery(['assistance-type', 'allowed'], () =>
  IntegrationModel.getAllowedAssistanceTypes()
);
const assistanceTypeStore = useAssistanceTypeStore();
const { data: dataDesc, isFetching } = useQuery(
  ['assistance-type', 'license-location'],
  () => assistanceTypeStore.getAtByLicenseLocation(),
  {
    ...globalUseQueryOptions(),
  }
);

const canAccessDisasterAdministration = computed(() => {
  return dataDesc.value?.data.find((at: any) => at.id === getDisasterAssistanceTypeId())?.enabled ?? false;
});
const canAccessFoodAdministration = computed(() => {
  return dataDesc.value?.data.find((at: any) => at.id === getFoodAssistanceTypeId())?.enabled ?? false;
});

const config = useRuntimeConfig();
const locationId = userStoreWithRefs.currentUser.value?.profile?.licensedLocationId;

// Parse location restrictions from environment variables
const parseLocationCondition = (conditionStr: string) => {
  if (!conditionStr) return () => true;
  const isNegated = conditionStr.startsWith('!');
  const cleanStr = isNegated ? conditionStr.substring(1) : conditionStr;

  // Parse tuples from string like "(10,3366)" into array [10,3366]
  const matches = cleanStr.match(/\(([^)]+)\)/);
  if (!matches || !matches[1]) return () => true;

  const locations = matches[1].split(',').map((n) => parseInt(n.trim(), 10));

  return (locId: number) => {
    const includes = locations.includes(locId);
    return isNegated ? !includes : includes;
  };
};

console.log(config.public.allowedFoodLocations);
// Create checking functions
// const isDisasterAllowed = parseLocationCondition(config.public.allowedDisasterLocations);
// const isFoodAllowed = parseLocationCondition(config.public.allowedFoodLocations);

// Use in conditional checks

const useApplication = useApplicationStore();
const { data: checkData } = useQuery(['check-integration'], () => useApplication.checkIntegrationEnabled(), {
  ...globalUseQueryOptions(),
});

type NavLeaf = { name: string; href: string };
type NavChild = NavLeaf & { child?: NavLeaf[] };
type NavItem = NavLeaf & { child?: NavChild[] };

const navigation = computed(() => {
  let routes: NavItem[] = [{ name: 'Dashboard', href: '/' }];
  if (
    (userStoreWithRefs.currentUser.value?.profile?.licensedLocationId &&
      userStoreWithRefs.isAdminForLocation.value === true) ||
    userStore.hasAnySAhelpPerms()
  ) {
    routes.push({ name: 'Applicants', href: getOldLink('applicants', '/applicants') });

    /*if (userStoreWithRefs.currentUser.value?.adminUserId && userStore.getIsCanManage()) {
      routes.push({
        name: 'Assistance Requests',
        href: '',
        child: [
          {
            name: 'Assistance Requests',
            href: getOldLink('applications-filter', '/applications?default-filter=true'),
          },
          {
            name: 'Bulk Update',
            href: getOldLink('applications-filter', '/applications/bulk-update-page'),
          },
          {
            name: 'Bulk Update History',
            href: getOldLink('applications-filter', '/applications/bulk-update-history'),
          },
        ],
      });
    } else */ if (userStore.getIsCanManage()) {
      routes.push({
        name: 'Assistance Requests',
        href: '',
        child: [
          {
            name: 'Assistance Requests',
            href: getOldLink('applications-filter', '/applications?default-filter=true'),
          },
          {
            name: 'Bulk Update',
            href: getOldLink('applications-filter', '/applications/bulk-update-page'),
          },
          {
            name: 'Bulk Update History',
            href: getOldLink('applications-filter', '/applications/bulk-update-history'),
          },
        ],
      });
    } else {
      routes.push({
        name: 'Assistance Requests',
        href: getOldLink('applications-filter', '/applications?default-filter=true'),
      });
    }

    routes.push({ name: 'Service Areas', href: getOldLink('service-areas', '/service-areas') });
    routes.push({ name: 'Funding Sources', href: getOldLink('funding-sources', '/funding-sources') });
    routes.push({ name: 'Funds', href: getOldLink('funds', '/funds') });

    const appointmentList: NavLeaf[] = [
      { name: 'Application Review', href: getOldLink('appointments', '/appointments') },
    ];

    // One generic by-id distribution-appointments link per allowed + distributable
    // assistance type (replaces the old hard-wired Food/Disaster appointment links),
    // mirroring the Reports > Distributions submenu.
    const distributionAppointments: NavLeaf[] = (allowedData.value?.data ?? [])
      .filter((at: any) => at.isDistributable)
      .map((at: any) => ({ name: at.name, href: `/admin/distribution-types/${at.id}/appointments` }));

    appointmentList.push(...distributionAppointments);

    if (!userOnlyHasPmtProcPerms.value) {
      routes.push({
        name: 'Appointments',
        href: '',
        child: appointmentList,
      });
    }

    const reportList: NavChild[] = [];

    // Distributions submenu: one generic by-id report per allowed + distributable
    // assistance type (replaces the old hard-wired Food/Disaster report links).
    const distributionReports: NavLeaf[] = (allowedData.value?.data ?? [])
      .filter((at: any) => at.isDistributable)
      .map((at: any) => ({ name: at.name, href: `/reports/distributions/${at.id}` }));

    if (distributionReports.length > 0) {
      reportList.push({ name: 'Distributions', href: '', child: distributionReports });
    }

    if (userStoreWithRefs.isAdminForLocation.value || userStore.isCaseManagerDirector()) {
      reportList.push({ name: 'Mass Communications', href: '/reports/mass-communications' });
    }
    reportList.push({ name: 'Household Members', href: '/reports/household-members' });
    reportList.push({ name: 'Custom Reports', href: '/admin/advanced-filters/report?modal=open' });

    if (reportList.length > 0) {
      routes.push({
        name: 'Reports',
        href: '',
        child: reportList,
      });
    }

    if (data.value?.data) {
      routes.push({ name: 'FAQs', href: '/faqs' });
    }
  }

  const adminLink = [
    { name: 'Assistance Types', href: '/admin/additional-assistance-types' },
    { name: 'Requisite Documents', href: '/admin/documents/required' },
    { name: 'Approval Notifications', href: '/admin/documents/notification' },
    { name: 'Funding Source Warnings', href: '/admin/documents/funding-source' },
  ];

  adminLink.push({ name: 'Custom Forms', href: '/admin/custom-forms?active=true&page=1&sort=name&order=asc' });
  adminLink.push({ name: 'Custom Questions', href: '/admin/custom-questions?active=true&page=1&sort=name&order=asc' });
  adminLink.push({ name: 'Applicant Notifications', href: '/admin/custom-notifications' });

  if (checkData.value?.data?.enabled) {
    adminLink.push({ name: 'Community Services', href: '/admin/community-services' });
  }

  if (userStoreWithRefs.isAdminForLocation.value) {
    routes.push({
      name: 'Admin',
      href: '',
      child: adminLink,
    });
  }

  adminLink.push({ name: 'Custom Weblinks', href: '/admin/custom-weblinks' });

  if (userStoreWithRefs.isAdminForLocation.value || userStore.isCaseManagerDirector()) {
    routes.push({
      name: 'Transactions',
      href: '/reports/fund-transactions',
    });
  }

  if (userStoreWithRefs.isSysAdmin.value) {
    routes.push({ name: 'Territory Config', href: '/dynamic-form/territory' });
  }

  if (userStoreWithRefs.hasOversight.value) {
    // update routes to only Organizational Stats dashboard
    routes = [{ name: 'Organizational Stats', href: '/oversight' }];
  }

  return routes;
});

const showWarning = () => {
  toastWarning('', 'You do not have permission to view this page.');
};
const fundTransactionStore = useFundTransactionStore();

// Active-state helper shared by the mobile menu; ignores '' (submenu parents) and '/' (home).
const isActive = (href: string) => {
  const path = router.currentRoute.value.path;
  if (!href || href === '/') return path === href;
  return path === href || path.includes(href);
};

// Close the mobile panel after navigating; mirrors the desktop fund-transactions side effect.
const onMobileLinkClick = (href: string, close: () => void) => {
  if (href === '/reports/fund-transactions') fundTransactionStore.setShownSelectFiltersModal(false);
  close();
};

const getOldLink = (queryIndex: string, query: string) => {
  const oldLink = localStorage.getItem('last-link-' + queryIndex);
  return oldLink ?? query;
};
</script>
